import React, { useMemo } from "react";
import { Chip, Divider, List, ListItem, Stack, Typography } from "@mui/material";
import { getOrParseDateTime } from "@cpt/shared";
import { formatDateOnly } from "utils/formatters/DateFormatters";
import { RateCardAssignmentStatus, type RateCardAssignmentHistoryEntry } from "client/api-v2";
import { RATE_CARD_STATUS_COLOR } from "../constants/rate-card-status-color";

function formatTimestamp(value: string): string {
  const dateTime = getOrParseDateTime(value).setZone("utc");
  return `${formatDateOnly(dateTime)} ${dateTime.toFormat("HH:mm")} UTC`;
}

function formatEffectiveSegment(entry: RateCardAssignmentHistoryEntry): string {
  if (entry.status === RateCardAssignmentStatus.Draft) return "(not yet activated)";
  if (!entry.effectiveFrom) return "(unknown)";

  const from = formatDateOnly(getOrParseDateTime(entry.effectiveFrom));
  const range = entry.effectiveTo
    ? `${from} → ${formatDateOnly(getOrParseDateTime(entry.effectiveTo))}`
    : `${from} → present`;
  return `effective ${range}`;
}

// Drafts have no EffectiveFrom yet — they're the newest pending change, so they sort first.
function effectiveFromMillis(entry: RateCardAssignmentHistoryEntry): number {
  return entry.effectiveFrom ? getOrParseDateTime(entry.effectiveFrom).toMillis() : Number.MAX_SAFE_INTEGER;
}

interface SubscriptionRateCardAssignmentHistoryProps {
  assignments: RateCardAssignmentHistoryEntry[];
}

export function SubscriptionRateCardAssignmentHistory({ assignments }: SubscriptionRateCardAssignmentHistoryProps) {
  const orderedHistory = useMemo(
    () =>
      [...assignments].sort(
        (a, b) =>
          effectiveFromMillis(b) - effectiveFromMillis(a) ||
          getOrParseDateTime(b.createdDate).toMillis() - getOrParseDateTime(a.createdDate).toMillis()
      ),
    [assignments]
  );

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Assignment history</Typography>
      {orderedHistory.length === 0 && <Typography color="text.secondary">No assignment history yet.</Typography>}
      {orderedHistory.length > 0 && (
        <List disablePadding sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
          {orderedHistory.map((entry, index) => (
            <React.Fragment key={entry.assignmentId}>
              <ListItem sx={{ display: "block", py: 1.5, px: 2 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                  <Chip label={entry.status} size="small" color={RATE_CARD_STATUS_COLOR[entry.status]} />
                  <Typography variant="subtitle2">{entry.rateCardCode}</Typography>
                  {entry.label && (
                    <Typography variant="body2" color="text.secondary">
                      {entry.label}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    · {formatEffectiveSegment(entry)}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {formatTimestamp(entry.createdDate)} by {entry.createdByUser} — &quot;{entry.reason}&quot;
                </Typography>
              </ListItem>
              {index < orderedHistory.length - 1 && <Divider sx={{ mx: 2 }} />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Stack>
  );
}
